import React, {useState, useEffect, useMemo, useRef, useCallback} from "react";
import {Modal, Upload, Button, Progress, Typography, Space, App as AntdApp, Tag, Image, Popconfirm, Divider, Spin, Checkbox, Affix, Select, Collapse, Alert} from "antd";
import {InboxOutlined, CheckCircleOutlined, DeleteOutlined, CloseOutlined, CheckSquareOutlined, CloseSquareOutlined, FolderOpenOutlined, ShrinkOutlined, ArrowsAltOutlined} from "@ant-design/icons";

import api from "@/api/axios.js";
import {useUploadRefs} from "../hooks/useUploadRefs.js";
import {fetchSettings} from "@/modules/settings/api/settingsApi.js";
import {normalizeWebpProfiles} from "@/modules/settings/utils/webpProfiles.js";
import RefEditorModal from "./RefEditorModal.jsx";
import {normalizeCanvasConfig} from "../utils/canvasConfig.js";
import {EditOutlined} from "@ant-design/icons";

const {Text} = Typography;

const ALLOWED_EXTENSIONS = [".png", ".jpg", ".jpeg", ".webp", ".psd", ".ai", ".pdf", ".tif", ".tiff"];

function isImageFile(filename)
{
	const ext = filename.toLowerCase().split(".").pop();
	return ALLOWED_EXTENSIONS.includes(`.${ext}`);
}

function normalizeGroupKey(groupValue)
{
	if (typeof groupValue !== "string")
	{
		return "";
	}

	return groupValue.replace(/\\/g, "/").trim();
}

function normalizeGroupKeys(groupValues)
{
	const values = Array.isArray(groupValues) ? groupValues : [groupValues];
	return [...new Set(values.map(normalizeGroupKey).filter(Boolean))];
}

function resolveRefGroupKeys(ref)
{
	const explicitGroups = normalizeGroupKeys([...(ref?.sourceGroups || []), ref?.sourceGroup]);
	if (explicitGroups.length)
	{
		return explicitGroups;
	}

	const sourceRelativePaths = normalizeGroupKeys([...(ref?.sourceRelativePaths || []), ref?.sourceRelativePath]);
	const pathGroups = sourceRelativePaths
		.filter((sourceRelativePath) => sourceRelativePath.includes("/"))
		.map((sourceRelativePath) => sourceRelativePath.split("/").slice(0, -1).join("/"))
		.filter(Boolean);
	if (pathGroups.length)
		return [...new Set(pathGroups)];

	const originalNamePath = normalizeGroupKey(ref?.originalName);
	const parts = originalNamePath.split("/").filter(Boolean);
	if (parts.length >= 3)
	{
		return [parts.slice(1, -1).join("/")];
	}

	return [];
}

function sortGroupKeys(keys)
{
	return [...keys].sort((a, b) => a.localeCompare(b, "it", {sensitivity: "base", numeric: true}));
}

// Hook SSE semplificato per monitorare il job
function useJobProgress(jobId)
{
	const [progress, setProgress] = useState(0);
	const [status, setStatus] = useState("queued");
	const [totals, setTotals] = useState({total: 0, completed: 0, errors: 0});
	const [refsSummary, setRefsSummary] = useState(null);

	useEffect(() =>
	{
		if (!jobId)
		{
			setProgress(0);
			setStatus("queued");
			setTotals({total: 0, completed: 0, errors: 0});
			setRefsSummary(null);
			return;
		}

		const API = import.meta.env.VITE_API_URL || "";
		const url = `${API}/api/events?topic=webp-jobs`;
		const source = new EventSource(url, {withCredentials: true});

		const handleUpdate = (event) =>
		{
			try
			{
				const payload = JSON.parse(event.data);
				if (payload.id !== jobId) return;

				setStatus(payload.status);

				setTotals(payload.totals || {total: 0, completed: 0, errors: 0});
				setRefsSummary(payload.refsSummary || null);

				const total = payload.totals?.total || 0;
				const completed = payload.totals?.completed || 0;
				const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
				setProgress(percent);
			}
			catch (err)
			{
				console.error("Errore parsing SSE job.update", err);
			}
		};

		source.addEventListener("job.created", handleUpdate);
		source.addEventListener("job.update", handleUpdate);

		source.onerror = (event) =>
		{
			console.error("Connessione SSE webp interrotta", event);
		};

		return () =>
		{
			source.close();
		};
	}, [jobId]);

	return {progress, status, totals, refsSummary};
}

export default function UploadRefsModal({flyerId, open, onClose, onComplete, onRefsCountChange})
{
	const {message} = AntdApp.useApp();
	const {uploadRefs, isUploading} = useUploadRefs(flyerId);
	const [fileList, setFileList] = useState([]);
	const [jobId, setJobId] = useState(null);
	const {progress, status, totals, refsSummary} = useJobProgress(jobId);
	const [isDrag, setIsDrag] = useState(false);
	// Il jobId viene azzerato a fine caricamento: l'esito va conservato a parte,
	// altrimenti il riepilogo sparirebbe subito dopo essere comparso.
	const [lastRefsSummary, setLastRefsSummary] = useState(null);

	// Refs gallery state
	const [refs, setRefs] = useState([]);
	const lastNotifiedRefsCount = useRef(null);

	useEffect(() =>
	{
		if (!open) return;
		const count = refs.length;
		if (lastNotifiedRefsCount.current === count) return;
		lastNotifiedRefsCount.current = count;
		onRefsCountChange?.(count);
	}, [refs.length, open, onRefsCountChange]);
	const [refsLoading, setRefsLoading] = useState(false);
	const [selectedRefIds, setSelectedRefIds] = useState([]);
	const [isBulkDeleting, setIsBulkDeleting] = useState(false);
	const bodyRef = useRef(null);
	const isFetchingRefs = useRef(false);
	const hasInitiallyLoaded = useRef(false);
	const latestRefsCount = useRef(0);
	const [expectedTotal, setExpectedTotal] = useState(null);
	const [profiles, setProfiles] = useState([]);
	const [profilesLoading, setProfilesLoading] = useState(false);
	const [selectedProfileId, setSelectedProfileId] = useState(null);

	// Editor State
	const [editingRef, setEditingRef] = useState(null);

	// Collapse state per raggruppamenti
	const [collapseActiveKeys, setCollapseActiveKeys] = useState([]);

	const canvasConfig = useMemo(() =>
	{
		const profile = profiles.find(p => p.id === selectedProfileId);
		return normalizeCanvasConfig({
			width: profile?.canvas?.width || 1080,
			height: profile?.canvas?.height || 1080,
			fill: profile?.backgroundColor || '#ffffff',
			transparency: profile?.transparency !== false,
			fillPercentage: profile?.canvas?.fillPercentage
		});
	}, [profiles, selectedProfileId]);

	const toggleSelection = useCallback((refId) =>
	{
		setSelectedRefIds((prev) =>
		{
			if (prev.includes(refId))
			{
				return prev.filter((id) => id !== refId);
			}

			return [...prev, refId];
		});
	}, []);

	const isSelected = useCallback((refId) =>
	{
		return selectedRefIds.includes(refId);
	}, [selectedRefIds]);

	const selectAllRefs = useCallback(() =>
	{
		if (!Array.isArray(refs) || refs.length === 0) return;
		setSelectedRefIds(refs.map((ref) => ref.id));
	}, [refs]);

	const clearSelection = useCallback(() =>
	{
		setSelectedRefIds([]);
	}, []);

	const fetchRefs = useCallback(async () =>
	{
		if (!flyerId) return;
		if (isFetchingRefs.current) return;
		isFetchingRefs.current = true;
		// Mostra spinner solo al primo caricamento, non durante il polling
		if (!hasInitiallyLoaded.current) setRefsLoading(true);
		const pageSize = 100; // limite max lato API
		let all = [];
		let skip = 0;
		let total = null;
		let iterations = 0;

		try
		{
			while (iterations < 50)
			{
				const {data} = await api.get(`/api/flyers/${flyerId}/refs`, {params: {limit: pageSize, skip}});
				const items = Array.isArray(data?.items) ? data.items : [];
				const currentTotal = Number.isFinite(data?.total) ? data.total : total;
				total = currentTotal ?? items.length ?? 0;
				if (Number.isFinite(currentTotal)) setExpectedTotal(currentTotal);
				all = all.concat(items);

				if (items.length === 0) break;
				if (Number.isFinite(total) && all.length >= total) break;
				skip += pageSize;
				iterations += 1;
			}

			setRefs(all);
		}
		catch (e)
		{
			console.error("Errore caricamento referenze:", e);
		}
		finally
		{
			setRefsLoading(false);
			isFetchingRefs.current = false;
			hasInitiallyLoaded.current = true;
		}

		return all;
	}, [flyerId]);

	const loadProfiles = useCallback(async () =>
	{
		setProfilesLoading(true);
		try
		{
			const settings = await fetchSettings();
			const normalized = normalizeWebpProfiles(settings?.webpProfiles || []);
			setProfiles(normalized);

			const defaultProfile = normalized.find((p) => p.isDefault) || normalized[0] || null;
			setSelectedProfileId((prev) =>
			{
				if (prev && normalized.some((p) => p.id === prev))
					return prev;
				return defaultProfile?.id || null;
			});
		}
		catch (e)
		{
			console.error("Errore caricamento profili WebP:", e);
			setProfiles([]);
			setSelectedProfileId(null);
		}
		finally
		{
			setProfilesLoading(false);
		}
	}, []);

	const handleClearRefs = () =>
	{
		setFileList([]);
		message.info("Referenze rimosse");
	};

	useEffect(() =>
	{
		latestRefsCount.current = refs.length;
	}, [refs.length]);

	const validFilesCount = useMemo(() =>
	{
		return fileList.filter(f => isImageFile(f.name)).length;
	}, [fileList]);

	useEffect(() =>
	{
		if (!open)
		{
			setFileList([]);
			setJobId(null);
			setLastRefsSummary(null);
			setRefs([]);
			hasInitiallyLoaded.current = false;
			lastNotifiedRefsCount.current = null;
		}
		else
		{
			fetchRefs();
			loadProfiles();
		}

		clearSelection();
		setIsBulkDeleting(false);

		if (bodyRef.current)
		{
			bodyRef.current.scrollTop = 0;
		}
	}, [open, clearSelection, fetchRefs, loadProfiles]);

	useEffect(() =>
	{
		if (!jobId)
		{
			return;
		}

		let completionTimer;

		if (status === "completed")
		{
			const replaced = refsSummary?.replaced || 0;
			message.success(replaced > 0
				? `Caricamento completato: ${replaced} ${replaced === 1 ? "foto sostituita" : "foto sostituite"}`
				: "Caricamento completato!");
			// Aggiorna la gallery dopo la conversione
			fetchRefs();
			completionTimer = setTimeout(() =>
			{
				onComplete?.();
				// Non chiudiamo automaticamente: lasciamo decidere al chiamante.
				// onClose();
			}, 800);
		}
		else if (status === "error")
		{
			message.error("Caricamento terminato con errori: alcune foto non sono state salvate.");
			fetchRefs();
		}

		return () =>
		{
			if (completionTimer)
			{
				clearTimeout(completionTimer);
			}
		};
	}, [status, jobId, fetchRefs, onComplete, refsSummary]);

	const handleUpload = async () =>
	{
		if (validFilesCount === 0)
		{
			message.warning("Seleziona almeno un file immagine valido");
			return;
		}

		try
		{
			const files = fileList.filter(f => isImageFile(f.name)).map(f => f.originFileObj || f);

			setLastRefsSummary(null);
			const result = await uploadRefs(files, {webpProfileId: selectedProfileId});
			setExpectedTotal((prev) => Math.max(prev || 0, latestRefsCount.current + files.length));
			setJobId(result.jobId);
			message.success("Conversione avviata!");
		}
		catch (err)
		{
			message.error(err.message || "Errore durante l'upload");
		}
	};

	const isProcessing = !!jobId && status !== "completed" && status !== "error";
	useEffect(() =>
	{
		if (!isProcessing)
		{
			return;
		}

		const poll = setInterval(fetchRefs, 2000);
		return () => clearInterval(poll);
	}, [isProcessing, fetchRefs]);

	const draggerProps = {
		name: "file",
		multiple: true,
		directory: true,
		beforeUpload: () => false,
		showUploadList: false,
		fileList,
		onChange: (info) => setFileList(info.fileList),
		onDrop: (e) => setIsDrag(true),
		accept: ".png,.jpg,.jpeg,.webp,.psd,.ai,.pdf,.tif,.tiff",
		disabled: isUploading || isProcessing || isBulkDeleting || isDrag
	};

	useEffect(() =>
	{
		if (!jobId)
		{
			return;
		}

		if (status === "completed" || status === "error")
		{
			if (refsSummary)
				setLastRefsSummary(refsSummary);
			setFileList([]);
			setJobId(null);
		}

	}, [status, jobId, refsSummary]);

	const handleDeleteRef = async (ref) =>
	{
		const container = bodyRef.current;
		const previousScrollTop = container ? container.scrollTop : 0;

		try
		{
			await api.delete(`/api/flyers/${flyerId}/refs/${ref.id}`);
			message.success("Referenza eliminata");
			setSelectedRefIds((prev) => prev.filter((id) => id !== ref.id));
			setRefs((prevRefs) =>
			{
				const nextRefs = prevRefs.filter((currentRef) => currentRef.id !== ref.id);

				requestAnimationFrame(() =>
				{
					if (bodyRef.current)
					{
						bodyRef.current.scrollTop = previousScrollTop;
					}
				});

				return nextRefs;
			});
		}
		catch (e)
		{
			message.error(e?.response?.data?.error || e.message || "Errore durante l'eliminazione");
		}
	};

	const handleBulkDelete = async () =>
	{
		if (selectedRefIds.length === 0 || isBulkDeleting)
		{
			return;
		}

		setIsBulkDeleting(true);

		const container = bodyRef.current;
		const previousScrollTop = container ? container.scrollTop : 0;

		const responses = await Promise.allSettled(
			selectedRefIds.map((refId) => api.delete(`/api/flyers/${flyerId}/refs/${refId}`))
		);

		const successIds = [];
		const errorReasons = [];

		responses.forEach((result, index) =>
		{
			const refId = selectedRefIds[index];
			if (result.status === "fulfilled")
			{
				successIds.push(refId);
			}
			else
			{
				errorReasons.push(result.reason);
			}
		});

		if (successIds.length > 0)
		{
			const successSet = new Set(successIds);
			setRefs((prevRefs) =>
			{
				const nextRefs = prevRefs.filter((ref) => !successSet.has(ref.id));

				requestAnimationFrame(() =>
				{
					if (bodyRef.current)
					{
						bodyRef.current.scrollTop = previousScrollTop;
					}
				});

				return nextRefs;
			});

			setSelectedRefIds((prev) => prev.filter((id) => !successSet.has(id)));
		}

		if (successIds.length > 0 && errorReasons.length === 0)
		{
			message.success(`${successIds.length} referenze eliminate`);
		}
		else if (successIds.length > 0 && errorReasons.length > 0)
		{
			message.warning(`${successIds.length} referenze eliminate, ${errorReasons.length} errori`);
		}
		else if (successIds.length === 0 && errorReasons.length > 0)
		{
			message.error(`${errorReasons.length} referenze non eliminate`);
		}

		if (errorReasons.length > 0)
		{
			console.error("Errori durante l'eliminazione massiva delle referenze", errorReasons);
		}

		setIsBulkDeleting(false);
	};

	const showProgress = !!jobId;
	const selectedCount = selectedRefIds.length;

	useEffect(() =>
	{
		setIsDrag(false);
	}, [validFilesCount, showProgress])

	useEffect(() =>
	{
		if (totals?.total > 0)
		{
			setExpectedTotal(totals.total);
		}
	}, [totals?.total]);

	useEffect(() =>
	{
		if (status !== "completed")
		{
			return;
		}

		let attempts = 0;
		let timer;

		const syncAllRefs = async () =>
		{
			attempts += 1;
			const items = await fetchRefs();
			const count = Array.isArray(items) ? items.length : latestRefsCount.current;
			const target = expectedTotal || count;
			const reached = target ? count >= target : false;

			if (!reached && attempts < 8)
			{
				timer = setTimeout(syncAllRefs, 800);
			}
		};

		syncAllRefs();

		return () =>
		{
			if (timer) clearTimeout(timer);
		};
	}, [status, expectedTotal, fetchRefs]);

	const groupedRefsState = useMemo(() =>
	{
		const groupedMap = new Map();
		const groupOrder = [];
		const ungrouped = [];

		refs.forEach((ref) =>
		{
			const sourceGroups = resolveRefGroupKeys(ref);
			if (!sourceGroups.length)
			{
				ungrouped.push(ref);
				return;
			}

			sourceGroups.forEach((sourceGroup) =>
			{
				if (!groupedMap.has(sourceGroup))
				{
					groupedMap.set(sourceGroup, []);
					groupOrder.push(sourceGroup);
				}

				groupedMap.get(sourceGroup).push(ref);
			});
		});

		return {
			groupedMap,
			groupOrder: sortGroupKeys(groupOrder),
			ungrouped,
			hasGroups: groupOrder.length > 0
		};
	}, [refs]);

	// Inizializza tutte le chiavi espanse quando cambiano i gruppi
	const allCollapseKeys = useMemo(() =>
	{
		const keys = [...groupedRefsState.groupOrder];
		if (groupedRefsState.ungrouped.length > 0) keys.push("__uncategorized__");
		return keys;
	}, [groupedRefsState.groupOrder, groupedRefsState.ungrouped.length]);

	useEffect(() =>
	{
		setCollapseActiveKeys(allCollapseKeys);
	}, [allCollapseKeys]);

	const renderRefCard = (ref) => (
		<div
			key={ref.id}
			style={{position: "relative", cursor: isBulkDeleting ? "not-allowed" : "pointer"}}
			onClick={() =>
			{
				if (!isBulkDeleting)
				{
					toggleSelection(ref.id);
				}
			}}
		>
			<Checkbox
				checked={isSelected(ref.id)}
				onChange={(event) =>
				{
					event.stopPropagation();
					toggleSelection(ref.id);
				}}
				disabled={isBulkDeleting}
				style={{position: "absolute", top: 4, left: 4, zIndex: 2, background: "rgba(255,255,255,0.9)", padding: "2px 6px", borderRadius: 4}}
			/>
			<Popconfirm
				title="Eliminare questa referenza?"
				okText="Sì"
				cancelText="No"
				onConfirm={() => handleDeleteRef(ref)}
				disabled={isBulkDeleting}
			>
				<Button
					type="text"
					danger
					size="small"
					shape="circle"
					icon={<CloseOutlined/>}
					style={{color: "white", position: "absolute", top: -8, right: 0, zIndex: 2, background: "rgba(255,0,0,0.7)"}}
					disabled={isBulkDeleting}
					onClick={(event) => event.stopPropagation()}
				/>
			</Popconfirm>
			<Button
				type="text"
				size="small"
				shape="circle"
				icon={<EditOutlined/>}
				style={{color: "white", position: "absolute", top: -8, right: 28, zIndex: 2, background: "rgba(24, 144, 255, 0.8)"}}
				disabled={isBulkDeleting}
				onClick={(e) =>
				{
					e.stopPropagation();
					setEditingRef(ref);
				}}
			/>
			<Image
				src={((ref.url || "").startsWith("/") ? ((import.meta.env.VITE_API_URL || "") + ref.url) : ref.url) + (ref.updatedAt ? `?t=${new Date(ref.updatedAt).getTime()}` : "")}
				alt={ref.originalName || ref.fileName}
				style={{width: 150, aspectRatio: "1 / 1", objectFit: "cover", borderRadius: 4, border: isSelected(ref.id) ? "2px solid #1677ff" : "1px solid #eee", transition: "border-color 0.2s ease"}}
				preview={false}
			/>
		</div>
	);

	return (
		<Modal
			open={open}
			onCancel={onClose}
			title="Referenze Prodotto"
			footer={[
				<Button key="cancel" onClick={onClose} disabled={isProcessing || isBulkDeleting}>
					{showProgress && status === "completed" ? "Chiudi" : "Annulla"}
				</Button>,
			]}
			maskClosable={false}
			width={900}
			style={{top: 24}}
			styles={{body: {paddingRight: 8}}}
		>
			<div ref={bodyRef} style={{maxHeight: 'calc(100vh - 260px)', overflowY: 'auto', paddingRight: 18}}>
				<Space direction="vertical" size={16} style={{width: "100%"}}>
					<div style={{width: '100%', margin: '0 auto'}}>
						<Upload.Dragger {...draggerProps}>
							<p className="ant-upload-drag-icon">
								<InboxOutlined/>
							</p>
							<p className="ant-upload-text">Trascina cartella qui o clicca per selezionare</p>
							<p className="ant-upload-hint">
								Formati supportati: PNG, JPG, JPEG, WebP, PSD, AI, PDF, TIF, TIFF
							</p>
						</Upload.Dragger>

						{isDrag && (
							<Space style={{marginTop: 8}}>
								<Spin size="small"/>
								<Text type="secondary">Upload in corso...</Text>
							</Space>
						)}

						{validFilesCount > 0 && !showProgress && (
							<Space direction="vertical" size={8} style={{width: "100%", alignItems: "center", marginTop: 8}}>
								<Space size={8}>
									<Tag color="blue" style={{height: 30, alignContent: 'center'}}>{validFilesCount} {validFilesCount === 1 ? "file" : "file"}</Tag>
									<Button
										danger
										icon={<DeleteOutlined/>}
										onClick={handleClearRefs}
										disabled={isUploading || isProcessing}
									>
										Rimuovi
									</Button>

									<Select
										showSearch
										placeholder="Seleziona profilo WebP"
										value={selectedProfileId}
										onChange={setSelectedProfileId}
										loading={profilesLoading}
										optionFilterProp="label"
										disabled={isUploading || isProcessing || isBulkDeleting}
										style={{minWidth: 260}}
										options={profiles.map((p) => ({
											value: p.id,
											label: p.name + (p.isDefault ? " (Default)" : "")
										}))}
										filterOption={(input, option) => (option?.label || "").toLowerCase().includes(input.toLowerCase())}
										notFoundContent="Nessun profilo WebP"
									/>
									<Button
										key="submit"
										type="dashed"
										onClick={handleUpload}
										loading={isUploading}
										disabled={validFilesCount === 0 || isProcessing || isBulkDeleting}
									>
										{isProcessing ? "Elaborazione..." : "Avvia Conversione"}
									</Button>
								</Space>
							</Space>
						)}

						{showProgress && (
							<Space direction="vertical" size={8} style={{width: "100%", marginTop: 8}}>
								{status !== "completed" && (
									<>
										<Text strong>Conversione in corso...</Text>
										<Progress
											percent={progress}
											status={status === "completed" ? "success" : "active"}
											strokeColor={status === "completed" ? "#52c41a" : undefined}
										/>
									</>
								)}

								<Space>
									<Text type="secondary">
										Completati: {totals.completed}/{totals.total || validFilesCount}
									</Text>
									{totals.errors > 0 && (
										<Text type="danger">Errori: {totals.errors}</Text>
									)}
								</Space>
							</Space>
						)}

						{lastRefsSummary && (
							<Space direction="vertical" size={8} style={{width: "100%", marginTop: 8}}>
								<Space>
									<CheckCircleOutlined style={{color: lastRefsSummary.failed?.length ? "#faad14" : "#52c41a"}}/>
									<Text type={lastRefsSummary.failed?.length ? "warning" : "success"}>
										{lastRefsSummary.created} nuove
										{lastRefsSummary.replaced > 0 && ` · ${lastRefsSummary.replaced} sostituite`}
										{lastRefsSummary.reused > 0 && ` · ${lastRefsSummary.reused} già presenti`}
										{lastRefsSummary.failed?.length > 0 && ` · ${lastRefsSummary.failed.length} non salvate`}
									</Text>
								</Space>

								{lastRefsSummary.replaced > 0 && (
									<Alert
										type="warning"
										showIcon
										message={`${lastRefsSummary.replaced} ${lastRefsSummary.replaced === 1 ? "foto aveva" : "foto avevano"} lo stesso nome di referenze già presenti`}
										description="Le versioni precedenti sono state sostituite da quelle appena caricate: vale sempre l'ultimo caricamento."
									/>
								)}

								{lastRefsSummary.failed?.length > 0 && (
									<Alert
										type="error"
										showIcon
										message={`${lastRefsSummary.failed.length} ${lastRefsSummary.failed.length === 1 ? "foto non è stata salvata" : "foto non sono state salvate"}`}
										description={
											<div style={{maxHeight: 120, overflowY: "auto"}}>
												{lastRefsSummary.failed.map((item, index) => (
													<div key={`${item.fileName || "errore"}-${index}`}>
														<Text code>{item.fileName || "—"}</Text> {item.reason}
													</div>
												))}
											</div>
										}
									/>
								)}
							</Space>
						)}
					</div>

					<Divider orientation="left" style={{margin: 0, marginBottom: 12}}>Referenze esistenti</Divider>

					<Affix offsetTop={0} target={() => bodyRef.current}>
						<div style={{zIndex: 10, paddingBottom: 0, backgroundColor: "white"}}>
							<Space
								align="center"
								style={{width: "100%", justifyContent: "space-between", marginBottom: 8}}
							>
								<Text type="secondary">Selezionate: {selectedCount}</Text>
								<Space>
									{groupedRefsState.hasGroups && (
										<>
											<Button
												size="small"
												type="default"
												icon={<ShrinkOutlined/>}
												onClick={() => setCollapseActiveKeys([])}
												disabled={collapseActiveKeys.length === 0 || isBulkDeleting}
											>
												Comprimi
											</Button>
											<Button
												size="small"
												type="default"
												icon={<ArrowsAltOutlined/>}
												onClick={() => setCollapseActiveKeys(allCollapseKeys)}
												disabled={collapseActiveKeys.length === allCollapseKeys.length || isBulkDeleting}
											>
												Espandi
											</Button>
										</>
									)}
									<Button
										size="small"
										type="default"
										onClick={selectAllRefs}
										icon={<CheckSquareOutlined/>}
										disabled={refs.length === 0 || selectedCount === refs.length || isBulkDeleting}
										style={{minWidth: 132}}
									>
										Seleziona tutti
									</Button>
									<Button
										size="small"
										type="default"
										icon={<CloseSquareOutlined/>}
										onClick={clearSelection}
										disabled={selectedCount === 0 || isBulkDeleting}
										style={{minWidth: 132}}
									>
										Deseleziona tutti
									</Button>
									<Popconfirm
										title="Eliminare le referenze selezionate?"
										okText="Sì"
										cancelText="No"
										onConfirm={handleBulkDelete}
										disabled={selectedCount === 0 || isBulkDeleting}
										okButtonProps={{loading: isBulkDeleting}}
									>
										<Button
											type="primary"
											danger
											icon={<DeleteOutlined/>}
											disabled={selectedCount === 0 || isBulkDeleting}
											loading={isBulkDeleting}
										>
											Elimina selezionate
										</Button>
									</Popconfirm>
								</Space>
							</Space>
						</div>
					</Affix>

					{refsLoading ? (
						<div style={{display: "flex", justifyContent: "center", padding: 12}}><Spin size="small"/></div>
					) : (
						groupedRefsState.hasGroups ? (
							<Space direction="vertical" size={12} style={{width: "100%"}}>
								<Collapse
									bordered={false}
									activeKey={collapseActiveKeys}
									onChange={(keys) => setCollapseActiveKeys(keys)}
									items={[
										...groupedRefsState.groupOrder.map((groupKey) =>
										{
											const groupRefs = groupedRefsState.groupedMap.get(groupKey) || [];
											return {
												key: groupKey,
												label: (
													<Space size={8}>
														<FolderOpenOutlined/>
														<Text strong>{groupKey}</Text>
														<Tag color="blue">{groupRefs.length}</Tag>
													</Space>
												),
												children: (
													<div style={{display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8}}>
														{groupRefs.map(renderRefCard)}
													</div>
												)
											};
										}),
										...(groupedRefsState.ungrouped.length > 0
											? [{
												key: "__uncategorized__",
												label: (
													<Space size={8}>
														<FolderOpenOutlined/>
														<Text strong>Non classificate</Text>
														<Tag color="default">{groupedRefsState.ungrouped.length}</Tag>
													</Space>
												),
												children: (
													<div style={{display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8}}>
														{groupedRefsState.ungrouped.map(renderRefCard)}
													</div>
												)
											}]
											: [])
									]}
								/>
							</Space>
						) : (
							<div style={{display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8}}>
								{refs.map(renderRefCard)}
							</div>
						)
					)}
				</Space>
			</div>

			{/* Editor Modal */}
			{editingRef && (
				<RefEditorModal
					open={!!editingRef}
					onClose={() => setEditingRef(null)}
					imageSrc={(() =>
					{
						// Usa originalUrl solo se il browser può renderizzarlo (no PSD, AI, PDF, TIFF)
						const NON_RENDERABLE = /\.(psd|ai|pdf|tiff?)$/i;
						const origPath = editingRef.originalUrl || '';
						const srcPath = (origPath && !NON_RENDERABLE.test(origPath)) ? origPath : (editingRef.url || '');
						return srcPath.startsWith('/') ? ((import.meta.env.VITE_API_URL || '') + srcPath) : srcPath;
					})()}
					canvasConfig={editingRef.canvasConfig || canvasConfig}
					initialComposition={editingRef.composition || null}
					onSave={async (newComposition) =>
					{
						try
						{
							console.log("Salvataggio modifiche per ref:", editingRef.id, newComposition);
							console.log("Canvas Config usato per salvataggio:", canvasConfig);

							await api.patch(`/api/flyers/${flyerId}/refs/${editingRef.id}`, {
								composition: newComposition,
								// Importante: usa il config della referenza se esiste (preserva sfondo blu), 
								// altrimenti fallback al config globale (che potrebbe essersi resettato a bianco default)
								canvasConfig: editingRef.canvasConfig || canvasConfig
							});

							message.success("Referenza aggiornata");
							fetchRefs(); // Ricarica la lista per aggiornare le anteprime (timestamp/cache busting se necessario)
						}
						catch (err)
						{
							console.error(err);
							message.error("Errore durante il salvataggio");
						}
					}}
				/>
			)}
		</Modal>
	);
}
